import apiUrl from "../../../constants";
import axios from "axios";
import CryptoJS from "crypto-js";
import VueElementLoading from "vue-element-loading";
import { BasicSelect } from "vue-search-select";
import download from "downloadjs";
import Paginate from "vuejs-paginate";

// OD columns for sector download (from GET /ntcmappingmaster/search).
// HUB-8353: Mot prefilled per row from OD routeMode.
const NTC_MAPPING_COLUMNS = [
  { key: "scan_location", header: "Scan location" },
  { key: "bag_origin", header: "Bag Origin" },
  { key: "bag_destination_id", header: "Bag destination Id" },
  { key: "bag_destination", header: "Bag destination" },
  { key: "ntc", header: "NTC" },
  { key: "secondary_ntc", header: "Secondary NTC" },
];

// Map OD routeMode (Air/Surface) → sector CSV Mot (AIR/SURFACE); upload accepts any case.
const motFromOdRouteMode = (routeMode) => {
  const normalized = String(routeMode || "").trim().toLowerCase();
  if (normalized === "air") return "AIR";
  if (normalized === "surface") return "SURFACE";
  return "";
};

// Sector columns left blank for the operator to fill before upload.
const SECTOR_EMPTY_COLUMNS = [
  { key: "Zone", header: "Zone" },
  { key: "Business Type", header: "Business Type" },
  { key: "Mot", header: "Mot" },
  { key: "Bag Type", header: "Bag Type" },
  { key: "Routing Cluster", header: "Routing Cluster" },
  { key: "Primary Sector", header: "Primary Sector" },
  { key: "Secondary Sector", header: "Secondary Sector" },
];

const DOWNLOAD_PAGE_LIMIT = 500;
const API_HEADERS = { Authorization: "authkey xB84JJ89Hd25" };

const SEARCH_FILTER_FIELDS = [
  "zone",
  "businessType",
  "mot",
  "bagType",
  "destination",
  "ntc",
];
const HUB_NAME_FILTER_FIELDS = new Set(["destination", "ntc"]);

const BUSINESS_TYPE_OPTIONS = [
  { value: "1", text: "Ecom" },
  { value: "2", text: "Cargo" },
];
const MOT_OPTIONS = [
  { value: "1", text: "Air" },
  { value: "2", text: "Surface" },
];

function createEmptySearchItems() {
  return SEARCH_FILTER_FIELDS.reduce((acc, field) => {
    acc[field] = { value: "", text: "" };
    return acc;
  }, {});
}

// Fields the operator can change from the Edit modal. Change detection is done
// against the row's original values so we only send fields actually touched.
const EDITABLE_TEXT_FIELDS = ["routingCluster", "primarySector", "secondarySector"];

// Full upload template = NTC mapping columns + sector columns the operator fills in.
const UPLOAD_COLUMNS = NTC_MAPPING_COLUMNS.map((col) => ({
  ...col,
  required: col.key !== "secondary_ntc",
})).concat(
  SECTOR_EMPTY_COLUMNS.map((col) => ({ ...col, required: true })),
);

export default {
  name: "NtcSectorMapping",
  components: {
    VueElementLoading,
    BasicSelect,
    download,
    Paginate,
  },
  data() {
    return {
      hubId: "",
      createdBy: "",
      isLoading: false,
      isLoadingUpload: false,
      failed: [],
      selectedFile: null,
      lastUploadSummary: null,
      uploadColumns: UPLOAD_COLUMNS,

      // Search dropdown options + selected items (GET /ntcsectormapping/search).
      hublist: [],
      hubZoneList: [],
      bagTypeList: [],
      businessTypeOptions: BUSINESS_TYPE_OPTIONS,
      motOptions: MOT_OPTIONS,
      searchItems: createEmptySearchItems(),
      isSearching: false,
      rows: [],
      pageno: 0,
      limit: 10,
      totalRows: 0,
      pageCount: 0,

      isEditModalShow: false,
      isSavingEdit: false,
      editForm: {
        sectorId: null,
        bagOrigin: "",
        scanLocation: "",
        destination: "",
        ntc: "",
        secondaryNtc: "",
        zone: "",
        businessType: "",
        mot: "",
        bagType: "",
        routingCluster: "",
        primarySector: "",
        secondarySector: "",
        isActive: true,
      },
      editOriginal: {
        routingCluster: "",
        primarySector: "",
        secondarySector: "",
      },
    };
  },
  computed: {
    searchFilterConfig() {
      return [
        {
          field: "zone",
          label: "Zone",
          options: this.hubZoneList,
          icon: "<i class='fa fa-university'></i>",
        },
        {
          field: "businessType",
          label: "Business Type",
          options: this.businessTypeOptions,
          icon: "<i class='fa fa-map-signs'></i>",
        },
        {
          field: "mot",
          label: "MOT",
          options: this.motOptions,
          icon: "<i class='fa fa-map-signs'></i>",
        },
        {
          field: "bagType",
          label: "Bag Type",
          options: this.bagTypeList,
          icon: "<i class='fa fa-map-signs'></i>",
        },
        {
          field: "destination",
          label: "Destination",
          options: this.hublist,
          icon: "<i class='fa fa-area-chart'></i>",
        },
        {
          field: "ntc",
          label: "NTC",
          options: this.hublist,
          icon: "<i class='fa fa-area-chart'></i>",
        },
      ];
    },
  },
  mounted() {
    const userdetailEncrypt = window.localStorage.getItem("userdetail");
    const bytes = CryptoJS.AES.decrypt(userdetailEncrypt.toString(), "Key");
    const plaintext = bytes.toString(CryptoJS.enc.Utf8);
    const userdetail = JSON.parse(plaintext);
    this.createdBy = userdetail[0].email;
    this.hubId = userdetail[0].HubId[0];
    this.loadSearchDropdowns();
    if (this.hubId) {
      this.searchRows();
    }
  },
  methods: {
    extractHubName(text) {
      return (text || "").split("(")[0].trim();
    },

    selectSearchFilter(field, item) {
      this.searchItems[field] = item || { value: "", text: "" };
    },

    buildSearchParams() {
      const params = {
        currentHubId: this.hubId,
        limit: this.limit,
        offset: this.pageno * this.limit,
      };

      SEARCH_FILTER_FIELDS.forEach((field) => {
        const text = (this.searchItems[field] && this.searchItems[field].text) || "";
        if (!text) return;
        params[field] = HUB_NAME_FILTER_FIELDS.has(field)
          ? this.extractHubName(text)
          : text;
      });

      return params;
    },

    resetSearchItems() {
      this.searchItems = createEmptySearchItems();
    },

    loadSearchDropdowns() {
      const requests = [
        axios.post(
          apiUrl.api_url_hubops + "expose/hubservicecenter/dropdwon/list",
          {},
          { headers: API_HEADERS },
        ),
        axios.post(apiUrl.api_url_hubops + "hubzone/list", {}, { headers: API_HEADERS }),
      ];

      if (this.hubId) {
        requests.push(
          axios.get(apiUrl.api_url_hubops + "fetchBagTypes?hubId=" + this.hubId, {
            headers: { ...API_HEADERS, requestfrom: "WEB" },
          }),
        );
      }

      Promise.all(requests)
        .then(([hubRes, zoneRes, bagRes]) => {
          if (hubRes.data.ResultCode === 100) {
            this.hublist = (hubRes.data.ResponseData || []).map((hub) => ({
              value: hub.HubID,
              text: hub.HubName + " (" + hub.HubID + ")",
            }));
          }

          const zones = (zoneRes.data.ResponseData || []);
          this.hubZoneList = zones.map((zone) => ({
            id: zone.hubzoneid,
            text: zone.hubzonename,
          }));

          if (bagRes && bagRes.data.ResultCode === 100) {
            const bagTypes = (bagRes.data.ResponseData || {}).bagType || [];
            this.bagTypeList = bagTypes.map((bag) => ({ text: bag.bagtype }));
          }
        })
        .catch((err) => console.error(err));
    },

    // GET /ntcmappingmaster/search — same contract as searchNTCMappingsHandler.
    searchNTCMappingsHandler(params) {
      return axios.get(apiUrl.api_url_hubops + "ntcmappingmaster/search", {
        params,
        headers: API_HEADERS,
      });
    },

    // GET /ntcsectormapping/search — list paginated NTC sector mapping rows.
    searchSectorNTCMappingsHandler(params) {
      return axios.get(apiUrl.api_url_hubops + "ntcsectormapping/search", {
        params,
        headers: API_HEADERS,
      });
    },

    searchRows() {
      this.isSearching = true;

      this.searchSectorNTCMappingsHandler(this.buildSearchParams())
        .then((res) => {
          this.isSearching = false;
          if (res.data.ResultCode === 100) {
            this.rows = res.data.ResponseData || [];
            const pagination = res.data.pagination || {};
            this.totalRows = pagination.totalCount || this.rows.length;
            this.pageCount = pagination.totalPages || 0;
          } else if (res.data.ResultCode === 101) {
            this.rows = [];
            this.totalRows = 0;
            this.pageCount = 0;
          } else {
            this.$alertify.error(res.data.ReturnMessage || "Search failed.");
          }
        })
        .catch((err) => {
          console.error(err);
          this.isSearching = false;
          this.$alertify.error((err && err.message) || "Error searching NTC sector mappings.");
        });
    },

    applySearch() {
      this.pageno = 0;
      this.searchRows();
    },

    resetSearch() {
      this.resetSearchItems();
      this.pageno = 0;
      this.searchRows();
    },

    getPaginationData(pageNum) {
      this.pageno = pageNum - 1;
      this.searchRows();
    },

    openEditRow(row) {
      const routingCluster = row.routingCluster || "";
      const primarySector = row.primarySectorName || row.primarySector || "";
      const secondarySector = row.secondarySectorName || row.secondarySector || "";

      this.editForm = {
        sectorId: row.sectorId,
        bagOrigin: row.bagOriginHubName || "",
        scanLocation: row.scanLocationHubName || "",
        destination: row.destinationHubName || "",
        ntc: row.ntcHubName || "",
        secondaryNtc: row.secondaryNtcHubName || "",
        zone: row.zone || "",
        businessType: row.businessType || "",
        mot: row.mot || "",
        bagType: row.bagType || "",
        routingCluster,
        primarySector,
        secondarySector,
        isActive: row.isActive !== undefined ? !!row.isActive : true,
      };
      this.editOriginal = { routingCluster, primarySector, secondarySector };
      this.isEditModalShow = true;
    },

    closeEditModal() {
      this.isEditModalShow = false;
      this.editForm = {
        sectorId: null,
        bagOrigin: "",
        scanLocation: "",
        destination: "",
        ntc: "",
        secondaryNtc: "",
        zone: "",
        businessType: "",
        mot: "",
        bagType: "",
        routingCluster: "",
        primarySector: "",
        secondarySector: "",
        isActive: true,
      };
      this.editOriginal = {
        routingCluster: "",
        primarySector: "",
        secondarySector: "",
      };
    },

    clearEditSecondarySector() {
      this.editForm.secondarySector = "";
    },

    submitEdit() {
      if (!this.editForm.sectorId) {
        this.$alertify.error("Missing sector id for edit.");
        return;
      }

      this.isSavingEdit = true;
      const payload = {
        sectorId: Number(this.editForm.sectorId),
        currentHubId: this.hubId,
        modifiedBy: this.createdBy,
        isActive: !!this.editForm.isActive,
      };

      EDITABLE_TEXT_FIELDS.forEach((field) => {
        if (this.editForm[field] !== this.editOriginal[field]) {
          payload[field] = this.editForm[field];
        }
      });

      axios
        .put(apiUrl.api_url_hubops + "ntcsectormapping/edit", payload, {
          headers: { Authorization: "authkey xB84JJ89Hd25" },
        })
        .then(
          (res) => {
            this.isSavingEdit = false;
            if (res.data.ResultCode == 100) {
              const msg = res.data.ReturnMessage || "";
              if (!/no changes to update/i.test(msg)) {
                this.$alertify.success(msg || "Sector mapping updated.");
              }
              this.closeEditModal();
              this.searchRows();
            } else {
              this.$alertify.error(res.data.ReturnMessage);
            }
          },
          (err) => {
            console.error(err);
            this.isSavingEdit = false;
            this.$alertify.error("Error Occured");
          },
        );
    },

    fetchAllNTCMappings() {
      if (!this.hubId) {
        return Promise.reject(new Error("Missing current hub id."));
      }

      const allRows = [];
      let offset = 0;
      let totalCount = null;

      const fetchPage = () => {
        const params = {
          currentHubId: this.hubId,
          limit: DOWNLOAD_PAGE_LIMIT,
          offset,
        };
        return this.searchNTCMappingsHandler(params).then((res) => {
          if (res.data.ResultCode === 101) {
            return allRows;
          }
          if (res.data.ResultCode !== 100) {
            return Promise.reject(
              new Error(res.data.ReturnMessage || "Failed to fetch NTC mappings."),
            );
          }

          const rows = res.data.ResponseData || [];
          allRows.push.apply(allRows, rows);

          const pagination = res.data.pagination || {};
          totalCount =
            pagination.totalCount != null ? pagination.totalCount : allRows.length;

          offset += DOWNLOAD_PAGE_LIMIT;
          if (allRows.length < totalCount && rows.length > 0) {
            return fetchPage();
          }
          return allRows;
        });
      };

      return fetchPage();
    },

    mapNTCRowToCsv(row) {
      const odRoute = row.routeMode || "";
      const laneMot = motFromOdRouteMode(odRoute);

      const csvRow = {
        scan_location: row.scanLocationHubName || "",
        bag_origin: row.bagOriginHubName || "",
        bag_destination_id:
          row.destinationHubId != null ? row.destinationHubId : "",
        bag_destination: row.destinationHubName || "",
        ntc: row.ntcHubName || "",
        secondary_ntc: row.secondaryNtcHubName || "",
      };

      SECTOR_EMPTY_COLUMNS.forEach((col) => {
        if (col.key === "Mot") {
          csvRow[col.key] = laneMot;
        } else {
          csvRow[col.key] = "";
        }
      });

      return csvRow;
    },

    downloadNTCFile() {
      this.isLoading = true;

      this.fetchAllNTCMappings()
        .then((rows) => {
          this.isLoading = false;

          if (!rows.length) {
            this.$alertify.error("No NTC mappings found to download.");
            return;
          }

          const csvFields = {};
          NTC_MAPPING_COLUMNS.concat(SECTOR_EMPTY_COLUMNS).forEach((col) => {
            csvFields[col.key] = col.header;
          });

          const exportData = rows.map((row) => this.mapNTCRowToCsv(row));
          this.jsonToCSV("NTC_Sector_Mapping.csv", exportData, csvFields);
        })
        .catch((err) => {
          console.error(err);
          this.isLoading = false;
          this.$alertify.error(
            (err && err.message) || "Error downloading NTC file.",
          );
        });
    },

    jsonToCSV(filename, data, csvfield, skipFilter = []) {
      let csvData = "";
      const fieldKeys = Object.keys(csvfield).filter(
        (key) => skipFilter.indexOf(key) === -1,
      );
      csvData += fieldKeys.map((key) => csvfield[key]).join(",") + "\r\n";
      data.forEach(function (item) {
        csvData += fieldKeys
          .map((key) => {
            let value = item[key];
            if (value === null || value === undefined) return "";
            let escapedCSV = value + "";
            if (escapedCSV.match(/[,"\n]/)) {
              escapedCSV = '"' + escapedCSV.replace(/\"/g, '""') + '"';
            }
            if (escapedCSV == "null") return "";
            return escapedCSV;
          })
          .join(",");
        csvData += "\r\n";
      });
      return this.export(csvData, filename, "application/csv");
    },

    export(data, filename, mime) {
      const blob = this.base64ToBlob(data, mime);
      download(blob, filename, mime);
    },

    base64ToBlob(data, mime) {
      const base64 = window.btoa(window.unescape(encodeURIComponent(data)));
      const bstr = atob(base64);
      let n = bstr.length;
      const u8arr = new Uint8ClampedArray(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    },

    uploadConfigFile(event) {
      this.failed = [];
      this.lastUploadSummary = null;
      this.selectedFile = event.target[0].elements[0].files[0];

      if (!this.selectedFile) {
        this.$alertify.error("Please select a file to upload.");
        return false;
      }

      if (this.selectedFile.size > 5242880) {
        this.$alertify.error(
          "Failed! Upload Max File Size Should Not Be Greater Than 5 MB",
        );
        return false;
      }

      if (
        !/\.(csv)$/i.test(this.selectedFile.name) &&
        !/\.(zip)$/i.test(this.selectedFile.name)
      ) {
        this.isLoadingUpload = false;
        this.$alertify.error(
          "Failed! Please Upload Only Valid Format: .csv or .zip",
        );
        return false;
      }

      this.isLoadingUpload = true;

      const fd = new FormData();
      fd.append("currentHubId", this.hubId);
      fd.append("uploadedBy", this.createdBy);
      fd.append("fileData", this.selectedFile);
      fd.append("fileName", this.selectedFile.name);

      axios
        .post(apiUrl.api_url_hubops + "ntcsectormapping/upload", fd, {
          headers: {
            Authorization: "authkey xB84JJ89Hd25",
          },
        })
        .then(
          (result) => {
            if (result.data.ResultCode == 100) {
              const data = result.data.ResponseData || {};
              this.lastUploadSummary = {
                totalRows:
                  data.totalRows != null ? data.totalRows : null,
                validCount:
                  data.validCount != null ? data.validCount : null,
                insertedCount:
                  data.insertedCount != null ? data.insertedCount : null,
                updatedCount:
                  data.updatedCount != null ? data.updatedCount : null,
              };

              const parts = [];
              if (this.lastUploadSummary.insertedCount != null) {
                parts.push(
                  `${this.lastUploadSummary.insertedCount} new mappings inserted`,
                );
              }
              if (this.lastUploadSummary.updatedCount != null) {
                parts.push(
                  `${this.lastUploadSummary.updatedCount} existing mappings updated`,
                );
              }
              const toast = parts.length
                ? `Upload complete: ${parts.join(", ")}.`
                : result.data.ReturnMessage || "Upload complete.";
              this.$alertify.success(toast);
              this.resetForm(null, true);
              this.pageno = 0;
              this.searchRows();
            } else {
              this.$alertify.error(result.data.ReturnMessage);
            }

            if (
              result &&
              result.data.failed &&
              result.data.failed.length > 0
            ) {
              this.failed = result.data.failed;
            }

            this.isLoadingUpload = false;
          },
          (error) => {
            console.error(error);
            this.isLoadingUpload = false;
            this.$alertify.error("Error Occured");
          },
        );
    },

    onUploadSubmit(event) {
      this.$validator
        .validateAll()
        .then((result) => {
          if (result) {
            this.uploadConfigFile(event);
            event.target.reset();
          }
        })
        .catch(() => {
          this.$alertify.error("Error Occured");
        });
    },

    exportFailedConfig() {
      if (!this.failed || this.failed.length === 0) {
        this.$alertify.error("No failed rows to export.");
        return;
      }

      const csvFields = this.uploadColumns.reduce((acc, col) => {
        acc[col.header] = col.header;
        return acc;
      }, {});
      csvFields.Reason = "Reason";

      this.jsonToCSV("ntc_sector_mapping_failed.csv", this.failed, csvFields);
    },

    resetForm(event, preserveSummary = false) {
      this.isLoadingUpload = false;
      this.failed = [];
      this.selectedFile = null;
      if (!preserveSummary) this.lastUploadSummary = null;
      this.$validator.reset();
      this.errors.clear();
      if (event && event.target && event.target.form) {
        event.target.form.reset();
      }
    },
  },
};



// WEBPACK FOOTER //
// ./src/views/components/NtcSectorMapping/NtcSectorMapping.js